import * as d3 from 'd3'

import { stopEvent } from '@aytm/lib/common/func'
import { transformHex } from '@aytm/app/stats/lib/render/colors'

import { calcPercent, calcPosition } from '@aytm/app/stats/page/question-views/cjc/lib/calc'

const ELLIPSIS = '...'

const FONT_FAMILY = 'Ubuntu, Calibri, Segoe, Helvetica, Arial'

let measureContext

// Where drawChart remembers the layout the svg was drawn for.
const LAYOUT_KEY = '__cjcLayout'

const drawChartBase = (svg, settings, chartId) => {
  // static for a given layout, and a layout change rebuilds the whole chart
  if (!svg.select('.group-shadow').empty()) return

  const gradientId = `group-batch-gradient-${chartId}`

  // Background
  svg
    .append('rect')
    .attr('x', 0)
    .attr('y', 0)
    .attr('width', settings.width)
    .attr('height', settings.height)
    .attr('fill', '#f2f2f2')

  // Shadow gradient group
  const shadowGroup = svg
    .append('g')
    .attr('class', 'group-shadow')
    .attr('transform', `translate(${settings.pw},${settings.pp})`)
    .style('pointer-events', 'none')

  const defs = shadowGroup.append('defs')

  const gradient = defs
    .append('linearGradient')
    .attr('id', gradientId)
    .attr('x1', '0%')
    .attr('y1', '0%')
    .attr('x2', '0%')
    .attr('y2', '100%')

  gradient.append('stop').attr('offset', 0).attr('stop-color', '#555555').attr('stop-opacity', 0.7)

  gradient.append('stop').attr('offset', 0.005).attr('stop-color', '#555555').attr('stop-opacity', 0.4)

  gradient.append('stop').attr('offset', 0.09).attr('stop-color', '#f2f2f2').attr('stop-opacity', 0)

  gradient.append('stop').attr('offset', 0.91).attr('stop-color', '#f2f2f2').attr('stop-opacity', 0)

  gradient.append('stop').attr('offset', 0.995).attr('stop-color', '#555555').attr('stop-opacity', 0.4)

  gradient.append('stop').attr('offset', 1).attr('stop-color', '#555555').attr('stop-opacity', 0.7)

  shadowGroup
    .append('rect')
    .attr('width', settings.width - settings.pw - settings.sw)
    .attr('height', settings.height - settings.pp)
    .attr('fill', `url(#${gradientId})`)
}

const drawChartPercents = (svg, state, settings, mode) => {
  const getScale = d => d.scale
  const getPoints = d => d.points
  const getPoint = d => d.point
  const getKeys = d => {
    const keys = Object.keys(d).reduce((acc, cur) => {
      if (cur === 'id' || (cur !== 'old' && !Array.isArray(d[cur]))) {
        acc.push(`${d[cur]}`)
      }
      return acc
    }, [])
    return keys.length ? d?.id && keys.join('-') : d?.id
  }

  // Percents group
  const g = svg
    .selectAll('.group-percents')
    .data([state.ps])
    .join('g')
    .attr('class', 'group-percents')
    .attr('transform', `translate(0,${settings.pp})`)

  // White background
  g.selectAll('rect.bg-white')
    .data([1])
    .join('rect')
    .attr('class', 'bg-white')
    .attr('x', 0)
    .attr('y', 0)
    .attr('width', settings.pw)
    .attr('height', settings.height - settings.pp)
    .attr('fill', '#ffffff')

  // Top background
  g.selectAll('rect.bg-top')
    .data([1])
    .join('rect')
    .attr('class', 'bg-top')
    .attr('x', 0)
    .attr('y', -20)
    .attr('width', settings.pw)
    .attr('height', settings.pp)
    .attr('fill', '#f2f2f2')

  // Current point indicator
  const gs = g.selectAll('.group-point').data(getPoint, getKeys)

  const gsEnter = gs.enter().append('g').attr('class', 'group-point')

  gsEnter.attr('transform', d => `translate(0,${d.old.y - settings.pp})`)

  gsEnter
    .append('path')
    .attr(
      'd',
      `M0,-${settings.sh / 2}L${settings.pw - 12},-${settings.sh / 2}L${settings.pw},0L${settings.pw - 12},${
        settings.sh / 2
      }L0,${settings.sh / 2}`
    )
    .attr('fill', '#000000')

  const text = gsEnter
    .append('text')
    .attr('x', (settings.pw - 6) / 2)
    .attr('y', (settings.sh - 22) / 2)
    .attr('text-anchor', 'middle')

  text
    .append('tspan')
    .text(d => d.t)
    .attr('fill', '#ffffff')
    .attr('font-size', 13)
    .attr('font-family', FONT_FAMILY)
    .attr('style', 'font-weight: bold')

  text
    .append('tspan')
    .text(mode === 'sum' ? '' : '%')
    .attr('fill', '#ffffff')
    .attr('font-size', 13)
    .attr('font-family', FONT_FAMILY)

  gsEnter
    .append('rect')
    .attr('x', settings.pw)
    .attr('y', -settings.sh / 2)
    .attr('width', settings.width - settings.pw)
    .attr('height', settings.sh)
    .attr('fill', '#000000')

  gs.exit().remove()

  // Scale lines
  const scaleGroups = g.selectAll('.group-scale').data(getScale, getKeys)

  scaleGroups
    .enter()
    .insert('g', '.group-point')
    .attr('class', 'group-scale')
    .attr('transform', d => `translate(0,${d.old.y - settings.pp})`)

  scaleGroups.exit().remove()

  // Percent markers
  const ps = g.selectAll('.group-scale').selectAll('.group-percent').data(getPoints, getKeys)

  const psEnter = ps
    .enter()
    .append('g')
    .attr('class', 'group-percent')
    .attr('index', d => d.id)

  psEnter
    .attr('transform', d => `translate(0,${d.old.y - settings.pp})`)
    .style('opacity', d => (d.v === 'visible' ? 1 : 0))
    .style('pointer-events', 'none')

  psEnter
    .append('line')
    .attr('x1', 0)
    .attr('y1', 0)
    .attr('x2', 4)
    .attr('y2', 0)
    .attr('stroke', '#e1e1e1')
    .attr('stroke-width', 1)

  psEnter
    .append('text')
    .text(d => d.t)
    .attr('x', 7)
    .attr('y', 3)
    .attr('fill', '#d4d4d4')
    .attr('font-size', 10)
    .attr('font-family', FONT_FAMILY)
    .attr('text-anchor', 'start')

  psEnter
    .append('line')
    .attr('x1', settings.pw - 14)
    .attr('y1', 0)
    .attr('x2', settings.width - settings.sw)
    .attr('y2', 0)
    .attr('stroke', '#e1e1e1')
    .attr('stroke-width', 1)

  ps.exit().remove()
}

const drawChartBatches = (svg, state, settings, mode, opts) => {
  const { hoveredBatch, scrollingBatch, onPackageChange, onHiddenBatchToggle } = opts

  const answer = opts.answer || {}
  const currentPackage = opts.currentPackage || []
  const persona = opts.persona || 0

  const cp = calcPercent(mode, calcPosition(mode, answer, persona, currentPackage))

  const getD = d => d
  const getWheel = d => d.wheel
  const getAvs = d => d.avs
  const getKeys = d => {
    const keys = Object.keys(d).reduce((acc, cur) => {
      if (cur === 'id' || (cur !== 'old' && !Array.isArray(d[cur]))) {
        acc.push(`${d[cur]}`)
      }
      return acc
    }, [])
    return keys.length ? d?.id && keys.join('-') : d?.id
  }

  // Items group
  const g = svg
    .selectAll('.group-batches')
    .data([state.bs])
    .join('g')
    .attr('class', 'group-batches')
    .attr('transform', `translate(${settings.pw},0)`)

  // Top background
  g.selectAll('rect.bg-batches')
    .data([1])
    .join('rect')
    .attr('class', 'bg-batches')
    .attr('x', -settings.pw)
    .attr('y', 0)
    .attr('width', settings.pw)
    .attr('height', settings.pp)
    .attr('fill', '#f2f2f2')

  // Item columns
  const gs = g.selectAll('.group-batch').data(getD, getKeys)

  const gsEnter = gs
    .enter()
    .append('g')
    .attr('class', 'group-batch')
    .attr('index', d => d.id)

  gsEnter.attr('transform', d => `translate(${d.x},0)`)

  // Handlers go on every column, not only new ones: a column kept from the previous draw would
  // otherwise read the package and callbacks it was built with.
  gs.merge(gsEnter)
    .on('mouseover', function (event, d) {
      if (!d.v) {
        svg.select(`#group-batch-${d.id}-header`).select('.group-batch-text').style('opacity', 1)
      } else {
        window.addEventListener('wheel', stopEvent, { passive: false })
        hoveredBatch.current = d.id
        expandBatch(d3.select(this), settings, hoveredBatch, scrollingBatch)
      }
    })
    .on('mouseout', function (event, d) {
      if (!d.v) {
        svg.select(`#group-batch-${d.id}-header`).select('.group-batch-text').style('opacity', 0)
      } else {
        window.removeEventListener('wheel', stopEvent, { passive: false })
        hoveredBatch.current = 0
        collapseBatch(d3.select(this), settings, hoveredBatch, scrollingBatch)
      }
    })
    .on('wheel', function (event, d) {
      wheelElement(event, d.id, { state, onPackageChange, currentPackage })
    })

  gsEnter
    .append('line')
    .attr('x1', 0)
    .attr('y1', 0)
    .attr('x2', 0)
    .attr('y2', settings.height)
    .attr('stroke', '#d4d4d4')
    .attr('stroke-width', 1)

  gsEnter
    .append('rect')
    .attr('x', 0)
    .attr('y', 0)
    .attr('width', d => d.w)
    .attr('height', settings.height)
    .attr('fill', '#f2f2f2')
    .attr('fill-opacity', 0)

  gs.exit().remove()

  // Hidden batch wheels
  const wgsHidden = gs
    .merge(gsEnter)
    .filter(d => !d.v)
    .selectAll('.group-batch-wheel')
    .data(getWheel, getKeys)

  const wgsHiddenEnter = wgsHidden
    .enter()
    .append('g')
    .attr('class', 'group-batch-wheel group-batch-wheel-hidden')
    .attr('index', d => `${d.b}-${d.id}`)

  wgsHiddenEnter.attr('transform', d => `translate(0,${d.old.y})`)

  wgsHiddenEnter
    .append('path')
    .attr(
      'd',
      d =>
        `M0,${d.old.d - settings.bp}L${d.w - 3},${d.old.d}L${d.w - 3},${d.old.d + d.old.h}L0,${
          d.old.d + d.old.h + settings.bp
        }`
    )
    .attr('fill', d => transformHex(d.c, [0, -10, 40]))
    .attr('fill-opacity', 0.8)

  wgsHiddenEnter
    .append('text')
    .text(d => d.avs.find(av => av.s)?.id || '')
    .attr('x', d => (d.w - 3) / 2)
    .attr('y', d => (d.avs.find(v => v.s)?.old?.y || 0) + 6)
    .attr('fill', '#ffffff')
    .attr('font-size', 14)
    .attr('font-family', FONT_FAMILY)
    .attr('text-anchor', 'middle')

  wgsHidden.exit().remove()

  // Visible batch wheels
  const wgsVisible = gs
    .merge(gsEnter)
    .filter(d => d.v)
    .selectAll('.group-batch-wheel')
    .data(getWheel, getKeys)

  const wgsVisibleEnter = wgsVisible
    .enter()
    .append('g')
    .attr('class', 'group-batch-wheel group-batch-wheel-visible')
    .attr('index', d => `${d.b}-${d.id}`)

  wgsVisibleEnter.attr('transform', d => `translate(0,${d.old.y})`)

  wgsVisible.merge(wgsVisibleEnter).on('mousedown', function (event, d) {
    selectElement(event, d3.select(this.parentNode), d.b, { state, scrollingBatch, onPackageChange, currentPackage })
  })

  wgsVisibleEnter
    .append('path')
    .attr(
      'd',
      d =>
        `M0,${d.old.d - settings.bp}L${settings.bp},${d.old.d}L${settings.bp},${d.old.h + d.old.d}L0,${
          d.old.h + d.old.d + settings.bp
        }`
    )
    .attr('fill', d => d.c)

  wgsVisibleEnter
    .append('rect')
    .attr('x', settings.bp)
    .attr('y', d => {
      const hb = hoveredBatch.current
      const sb = scrollingBatch.current
      if (sb === d.b || hb === d.b) return d.old.r[2]
      return d.h === settings.sh ? d.old.d : d.old.r[0]
    })
    .attr('width', d => d.w - settings.bp)
    .attr('height', d => {
      const hb = hoveredBatch.current
      const sb = scrollingBatch.current
      if (sb === d.b || hb === d.b) return d.old.r[3]
      return d.h === settings.sh ? d.old.h : d.old.r[1]
    })
    .attr('fill', d => transformHex(d.c, [0, 0, 35]))
    .attr('fill-opacity', 0.4)

  // Popup group
  const popup = wgsVisibleEnter
    .append('g')
    .attr('class', 'group-batch-popup')
    .attr('transform', 'translate(-600,-600)')
    .style('opacity', 0)

  popup.append('rect').attr('x', 0).attr('y', 0).attr('width', 8).attr('height', 8).attr('fill', '#303030')

  popup
    .append('text')
    .attr('x', 4)
    .attr('y', 14)
    .attr('fill', '#ffffff')
    .attr('font-size', settings.fs1)
    .attr('font-family', FONT_FAMILY)
    .attr('text-anchor', 'start')

  wgsVisible.exit().remove()

  // Answer variant pointers
  const avPointers = wgsVisible.merge(wgsVisibleEnter).selectAll('.group-batch-av-pointer').data(getAvs, getKeys)

  const avPointersEnter = avPointers
    .enter()
    .append('g')
    .attr('class', 'group-batch-av-pointer')
    .attr('index', d => `${d.b}-${d.id}`)

  avPointersEnter.attr('transform', d => `translate(${settings.bp},${d.old.y})`)

  avPointersEnter
    .append('path')
    .attr('d', `M-${settings.bp},-${(3 * settings.bp) / 4}L0,0L-${settings.bp},${(3 * settings.bp) / 4}`)
    .attr('fill', '#000000')

  avPointers.exit().remove()

  // Answer variants
  const avGroups = wgsVisible.merge(wgsVisibleEnter).selectAll('.group-batch-av').data(getAvs, getKeys)

  const avGroupsEnter = avGroups
    .enter()
    .append('g')
    .attr('class', 'group-batch-av')
    .attr('index', d => `${d.b}-${d.id}`)

  avGroupsEnter
    .attr('transform', d => {
      const hb = hoveredBatch.current
      const sb = scrollingBatch.current
      const offset = sb === d.b || hb === d.b ? d.old.e : d.old.d
      return `translate(${settings.bp},${d.old.y + offset})`
    })
    .style('opacity', d => {
      const hb = hoveredBatch.current
      const sb = scrollingBatch.current
      if (sb === d.b || hb === d.b) return 1
      return d.old.v === 'visible' ? 1 : 0
    })
    .style('cursor', d => (d.s ? 'default' : 'pointer'))

  avGroups
    .merge(avGroupsEnter)
    .on('mouseover', function (event, d) {
      if (d.s) return

      d3.select(this).select('rect').attr('fill-opacity', 0.6)

      const dp = cp - d.p
      const t = `${dp > 0 ? '-' : '+'}${Math.abs(
        Math.round((mode === 'sum' ? 100 : 10) * dp) / (mode === 'sum' ? 100 : 10)
      )}${mode === 'sum' ? '' : '%'}`

      const popupEl = d3.select(this.parentNode).select('.group-batch-popup')
      const textEl = popupEl.select('text').text(t)
      const bbox = textEl.node().getBBox()

      popupEl
        .select('rect')
        .attr('width', bbox.width + 8)
        .attr('height', bbox.height + 8)

      // beside the level where it is drawn: an expanded column moves its levels by their expanded offset.
      // A level under the pointer means its column is hovered; hoveredBatch cannot say so here, because the
      // column's mouseout, bubbling from the element the pointer left, has just reset it.
      const column = this.closest('.group-batch')
      const expands = Boolean(column && d3.select(column).datum()?.v)
      const offset = scrollingBatch.current === d.b || expands ? d.e : d.d
      popupEl
        .attr('transform', `translate(-${bbox.width + 8},${d.y + offset - (bbox.height + 8) / 2})`)
        .style('opacity', 1)
    })
    .on('mouseout', function (event, d) {
      if (d.s) return

      d3.select(this).select('rect').attr('fill-opacity', 0.4)

      const popupEl = d3.select(this.parentNode).select('.group-batch-popup')
      popupEl.select('text').text('').attr('dy', 0)
      popupEl.select('rect').attr('width', 8).attr('height', 8)
      popupEl.attr('transform', 'translate(-600,-600)').style('opacity', 0)
    })
    .on('click', function (event, d) {
      if (d.s) return

      const popupEl = d3.select(this.parentNode).select('.group-batch-popup')
      popupEl.select('text').text('').attr('dy', 0)
      popupEl.select('rect').attr('width', 8).attr('height', 8)
      popupEl.attr('transform', 'translate(-600,-600)').style('opacity', 0)

      onPackageChange(d.b, d.id)
    })

  avGroupsEnter
    .append('rect')
    .attr('x', 0)
    .attr('y', d => -d.old.h / 2)
    .attr('width', d => d.w - settings.bp)
    .attr('height', d => d.old.h)
    .attr('fill', d => d.c)
    .attr('fill-opacity', 0.4)

  const avText = avGroupsEnter
    .append('text')
    .attr('class', 'wrapped')
    .attr('t', d => d.t)
    .attr('x', 4)
    .attr('y', d => (d.old.s ? 6 : 4))
    .attr('fill', d => (d.s ? '#ffffff' : '#000000'))
    .attr('font-size', d => (d.s ? settings.fs2 : settings.fs1))
    .attr('font-family', FONT_FAMILY)
    .attr('text-anchor', 'start')

  avText.each(function (d) {
    wrapText(d3.select(this), d.w - settings.bp - 8, d.t, d.s ? settings.fs2 : settings.fs1)
  })

  avGroupsEnter.append('title').text(d => d.t)

  avGroups.exit().remove()

  // Item headers
  const headers = g.selectAll('.group-batch-header').data(getD, getKeys)

  const headersEnter = headers
    .enter()
    .append('g')
    .attr('id', d => `group-batch-${d.id}-header`)
    .attr('class', 'group-batch-header')
    .attr('index', d => d.id)

  headersEnter.attr('transform', d => `translate(${d.x},0)`).style('cursor', 'pointer')

  headers
    .merge(headersEnter)
    .on('mouseover', function () {
      d3.select(this).select('.group-batch-text').style('opacity', 1)
    })
    .on('mouseout', function () {
      d3.select(this).select('.group-batch-text').style('opacity', 0)
    })
    .on('click', (event, d) => {
      onHiddenBatchToggle(d.id)
    })

  headersEnter
    .append('rect')
    .attr('x', 0)
    .attr('y', 0)
    .attr('width', d => d.w)
    .attr('height', settings.pp - 1)
    .attr('fill', '#e6e6e6')

  headersEnter
    .append('line')
    .attr('x1', 0)
    .attr('y1', 0)
    .attr('x2', 0)
    .attr('y2', settings.pp)
    .attr('stroke', '#d4d4d4')
    .attr('stroke-width', 1)

  headersEnter
    .append('line')
    .attr('x1', 0)
    .attr('y1', settings.pp - 1)
    .attr('x2', d => d.w)
    .attr('y2', settings.pp - 1)
    .attr('stroke', '#d4d4d4')
    .attr('stroke-width', 1)

  const headerText = headersEnter
    .append('text')
    .attr('class', 'wrapped')
    .attr('t', d => (d.v ? `${d.id}. ${d.t}` : ''))
    .attr('x', 4)
    .attr('y', 13)
    .attr('fill', '#000000')
    .attr('font-size', 12)
    .attr('font-family', FONT_FAMILY)
    .attr('text-anchor', 'start')

  headerText.each(function (d) {
    wrapText(d3.select(this), d.w - 8, d.v ? `${d.id}. ${d.t}` : '', 12)
  })

  // Eye icon for hidden batches
  const iconGroup = headersEnter.append('g').attr('transform', 'translate(1,0)')

  iconGroup
    .append('path')
    .attr('transform', 'scale(0.25)')
    .attr(
      'd',
      'M36.057,17.458c-12.846,0-24.815,6.07-33.693,17.101v2.196c8.878,11.029,20.847,17.104,33.693,17.104 c12.844,0,24.81-6.076,33.694-17.104v-2.196C60.866,23.528,48.9,17.458,36.057,17.458z M33.969,22.48 c0.938-0.25,1.898,0.298,2.153,1.233c0.252,0.934-0.301,1.897-1.232,2.152c-2.979,0.806-5.371,3.149-6.24,6.111 c-0.224,0.765-0.923,1.262-1.681,1.262c-0.165,0-0.33-0.022-0.497-0.074c-0.929-0.273-1.46-1.244-1.187-2.176 C26.494,26.865,29.826,23.604,33.969,22.48z M36.057,50.672c-11.673,0-21.787-5.317-30.052-15.019 c5.689-6.675,12.259-11.273,19.609-13.491c-2.982,2.795-4.862,6.759-4.862,11.159c0,8.447,6.865,15.315,15.306,15.315 c8.438,0,15.303-6.868,15.303-15.315c0-4.396-1.871-8.353-4.851-11.15c7.308,2.224,13.823,6.819,19.507,13.482 C57.751,45.355,47.728,50.672,36.057,50.672z'
    )
    .attr('fill', '#333333')
    .attr('fill-opacity', d => (d.v ? 0 : 1))

  // Show/hide text overlay
  const batchText = headersEnter
    .append('g')
    .attr('class', 'group-batch-text')
    .attr('transform', d => `translate(0,${d.v ? 0 : settings.pp})`)
    .style('opacity', 0)

  batchText
    .append('rect')
    .attr('x', d => (d.v ? 1 : 0))
    .attr('y', 0)
    .attr('width', d => d.w)
    .attr('height', d => (d.v ? settings.pp - 1 : settings.height - settings.pp))
    .attr('fill', d => (d.v ? '#e6e6e6' : '#ffffff'))
    .attr('fill-opacity', d => (d.v ? 1 : 0.7))

  const batchTextLabel = batchText
    .append('text')
    .attr('x', d => (d.v ? 4 : settings.pp + 2))
    .attr('y', d => (d.v ? 14 : -5))
    .attr('text-anchor', 'start')
    .attr('transform', d => (d.v ? '' : 'rotate(90)'))

  batchTextLabel
    .append('tspan')
    .text(d => (d.v ? 'HIDE' : 'SHOW'))
    .attr('fill', '#000000')
    .attr('font-size', d => (d.v ? 12 : 14))
    .attr('font-family', FONT_FAMILY)

  batchTextLabel
    .append('tspan')
    .text(d => (d.v ? '' : d.t))
    .attr('dx', 4)
    .attr('fill', '#000000')
    .attr('font-size', d => (d.v ? 12 : 14))
    .attr('font-family', FONT_FAMILY)
    .attr('style', 'font-weight: bold')

  headers.exit().remove()
}

// The sidebar's shapes are built once per chart and only restyled on later draws, as the
// pre-React chart did: rebuilding them on every package step made the panel flicker.
const buildChartSidebar = (sidebar, settings, iconShadowId) => {
  const filter = sidebar.append('defs').append('filter').attr('id', iconShadowId).attr('height', '110%')

  filter.append('feGaussianBlur').attr('in', 'SourceAlpha').attr('stdDeviation', 4).attr('result', 'blur')

  filter.append('feOffset').attr('in', 'blur').attr('dx', 0).attr('dy', 0).attr('result', 'offsetBlur')

  const merge = filter.append('feMerge')
  merge.append('feMergeNode').attr('in', 'offsetBlur')
  merge.append('feMergeNode').attr('in', 'SourceGraphic')

  sidebar
    .append('line')
    .attr('x1', 1)
    .attr('y1', 0)
    .attr('x2', 1)
    .attr('y2', settings.height)
    .attr('stroke', '#d4d4d4')
    .attr('stroke-width', 2)

  sidebar
    .append('rect')
    .attr('x', 1)
    .attr('y', 0)
    .attr('width', settings.sw - 1)
    .attr('height', settings.height)
    .attr('fill', '#ffffff')
}

const buildPackageControls = (pkg, settings, iconShadowId) => {
  pkg
    .append('g')
    .attr('class', 'group-next-package')
    .attr('transform', `translate(1,${-settings.sh / 2 - 26})`)
    .style('cursor', 'pointer')
    .append('path')
    .attr('d', `M2,0L${(settings.sw - 1) / 2},-10L${settings.sw - 3},0`)
    .attr('fill', '#d6d6d6')

  const bestPkg = pkg
    .append('g')
    .attr('class', 'group-best-package')
    .attr('transform', `translate(1,${-settings.sh / 2 - 12})`)
    .style('cursor', 'pointer')

  bestPkg
    .append('rect')
    .attr('x', 0)
    .attr('y', -10)
    .attr('width', settings.sw - 1)
    .attr('height', 20)

  bestPkg
    .append('text')
    .text('BEST')
    .attr('x', (settings.sw - 1) / 2)
    .attr('y', 4)
    .attr('font-size', 11)
    .attr('font-family', FONT_FAMILY)
    .attr('text-anchor', 'middle')

  // Current package icon
  const iconGroup = pkg.append('g').attr('transform', 'translate(1,0)')

  iconGroup
    .append('rect')
    .attr('x', 0)
    .attr('y', -settings.sh / 2)
    .attr('width', settings.sw - 1)
    .attr('height', settings.sh)
    .attr('fill', '#29abe2')

  iconGroup
    .append('path')
    .attr('transform', `translate(7,${-settings.sh / 2}) scale(0.45)`)
    .attr(
      'd',
      'M56.35,16.2l-12-6.92A14.4,14.4,0,0,0,30,9.28L18,16.2a14.39,14.39,0,0,0-7.18,12.43V42.47A14.39,14.39,0,0,0,18,54.9l12,6.92a14.39,14.39,0,0,0,14.35,0l12-6.92a14.39,14.39,0,0,0,7.18-12.43V28.63A14.39,14.39,0,0,0,56.35,16.2Zm4.77,26.27a12,12,0,0,1-6,10.35l-12,6.92a12,12,0,0,1-12,0l-12-6.92a12,12,0,0,1-6-10.35V28.63a12,12,0,0,1,6-10.35l12-6.92a12,12,0,0,1,12,0l12,6.92a12,12,0,0,1,6,10.35Zm-28-17.58L25.39,30.2V49.58L18.52,45.2V25.77l.1.07,8.28-5.22ZM37,22.19,31,18l6.19-3.9,6,3.8Zm7.71,10.69,0,0,0,0V52.19L37.19,57l-7.58-4.77V32.92l-.17-.11L37,27.58l7.62,5.24.09-.05Zm11-7,.1-.07V45.2l-7,4.51V30.3L41,24.89l6.35-4.36Z'
    )
    .attr('fill', '#ffffff')
    .attr('filter', `url(#${iconShadowId})`)

  const worstPkg = pkg
    .append('g')
    .attr('class', 'group-worst-package')
    .attr('transform', `translate(1,${settings.sh / 2 + 12})`)
    .style('cursor', 'pointer')

  worstPkg
    .append('rect')
    .attr('x', 0)
    .attr('y', -10)
    .attr('width', settings.sw - 1)
    .attr('height', 20)

  worstPkg
    .append('text')
    .text('WORST')
    .attr('x', (settings.sw - 1) / 2)
    .attr('y', 4)
    .attr('font-size', 11)
    .attr('font-family', FONT_FAMILY)
    .attr('text-anchor', 'middle')

  pkg
    .append('g')
    .attr('class', 'group-previous-package')
    .attr('transform', `translate(1,${settings.sh / 2 + 26})`)
    .style('cursor', 'pointer')
    .append('path')
    .attr('d', `M2,0L${(settings.sw - 1) / 2},10L${settings.sw - 3},0`)
    .attr('fill', '#d6d6d6')
}

// Restyles one BEST / WORST button; its handlers are rebound on every draw so they read the
// package drawn now, not the one the button was built with.
const updateExtremeButton = (button, isActive, onPick) => {
  button.select('rect').attr('fill', isActive ? '#29abe2' : '#d6d6d6')
  button.select('text').attr('fill', isActive ? '#ffffff' : '#444444')

  button
    .on('mouseover', function () {
      if (isActive) return
      d3.select(this).select('rect').attr('fill', '#29abe2')
      d3.select(this).select('text').attr('fill', '#ffffff')
    })
    .on('mouseout', function () {
      if (isActive) return
      d3.select(this).select('rect').attr('fill', '#d6d6d6')
      d3.select(this).select('text').attr('fill', '#444444')
    })
    .on('click', () => {
      onPick()
    })
}

const updateStepArrow = (arrow, isHidden, onStep) => {
  arrow
    .style('opacity', isHidden ? 0 : 1)
    .on('mouseover', function () {
      d3.select(this).select('path').attr('fill', '#29abe2')
    })
    .on('mouseout', function () {
      d3.select(this).select('path').attr('fill', '#d6d6d6')
    })
    .on('click', () => {
      onStep(true)
    })
}

const drawChartSidebar = (svg, state, settings, currentPackage, mainPackages, opts) => {
  const { chartId, onNextPackage, onPrevPackage, onBestPackage, onWorstPackage } = opts
  // Scoped to this chart: a filter id shared by every conjoint on the page resolves to whichever
  // copy comes first in the document.
  const iconShadowId = `group-icon-shadow-${chartId}`

  let delta = 0

  const sidebar = svg
    .selectAll('.group-sidebar')
    .data([1])
    .join(enter => {
      const created = enter.append('g').attr('class', 'group-sidebar')
      buildChartSidebar(created, settings, iconShadowId)
      return created
    })
    .attr('transform', `translate(${settings.width - settings.sw},0)`)

  sidebar
    .on('mouseover', () => {
      delta = 0
      window.addEventListener('wheel', stopEvent, { passive: false })
    })
    .on('mouseout', () => {
      delta = 0
      window.removeEventListener('wheel', stopEvent, { passive: false })
    })
    .on('wheel', event => {
      delta += event.deltaY
      if (Math.abs(delta) > 100) {
        if (Math.sign(delta) < 0) {
          onNextPackage(false)
        } else {
          onPrevPackage(false)
        }
        delta = 0
      }
    })

  // Package navigation; a group that already exists keeps its place and the animation moves it on
  const pkg = sidebar
    .selectAll('.group-package')
    .data([state.cy])
    .join(enter => {
      // x starts where the animation leaves it, so the icon does not step sideways on its first move
      const created = enter
        .append('g')
        .attr('class', 'group-package')
        .attr('transform', d => `translate(0,${d.old.y})`)
      buildPackageControls(created, settings, iconShadowId)
      return created
    })

  const isBest = currentPackage.join(',') === mainPackages[0]?.join(',')
  const isWorst = currentPackage.join(',') === mainPackages[4]?.join(',')

  updateStepArrow(pkg.select('.group-next-package'), isBest, onNextPackage)
  updateExtremeButton(pkg.select('.group-best-package'), isBest, onBestPackage)
  updateExtremeButton(pkg.select('.group-worst-package'), isWorst, onWorstPackage)
  updateStepArrow(pkg.select('.group-previous-package'), isWorst, onPrevPackage)
}

const animateChart = (svg, state, settings, animationTime, opts) => {
  const { hoveredBatch, scrollingBatch } = opts
  const easing = d3.easeLinear

  // Animate percent groups
  svg
    .select('.group-percents')
    .selectAll('.group-point')
    .transition()
    .duration(animationTime)
    .ease(easing)
    .attr('transform', d => `translate(0,${d.y - settings.pp})`)

  svg
    .select('.group-percents')
    .selectAll('.group-scale')
    .transition()
    .duration(animationTime)
    .ease(easing)
    .attr('transform', d => `translate(0,${d.y - settings.pp})`)

  svg
    .select('.group-percents')
    .selectAll('.group-percent')
    .transition()
    .duration(animationTime)
    .ease(easing)
    .attr('transform', d => `translate(0,${d.y - settings.pp})`)

  // Animate sidebar
  svg
    .select('.group-sidebar')
    .selectAll('.group-package')
    .transition()
    .duration(animationTime)
    .ease(easing)
    .attr('transform', d => `translate(0,${d.y})`)

  // Animate batch wheels
  const bs = svg.select('.group-batches').selectAll('.group-batch')

  // Hidden wheels
  bs.selectAll('.group-batch-wheel.group-batch-wheel-hidden')
    .transition()
    .duration(animationTime)
    .ease(easing)
    .attr('transform', d => `translate(0,${d.y})`)
    .select('path')
    .attr('d', d => `M0,${d.d - settings.bp}L${d.w - 3},${d.d}L${d.w - 3},${d.d + d.h}L0,${d.d + d.h + settings.bp}`)

  bs.selectAll('.group-batch-wheel.group-batch-wheel-hidden')
    .select('text')
    .transition()
    .duration(animationTime)
    .ease(easing)
    .attr('y', d => (d.avs.find(v => v.s)?.y || 0) + 6)

  // Visible wheels
  bs.selectAll('.group-batch-wheel.group-batch-wheel-visible')
    .transition()
    .duration(animationTime)
    .ease(easing)
    .attr('transform', d => `translate(0,${d.y})`)
    .select('path')
    .attr(
      'd',
      d => `M0,${d.d - settings.bp}L${settings.bp},${d.d}L${settings.bp},${d.h + d.d}L0,${d.h + d.d + settings.bp}`
    )

  bs.selectAll('.group-batch-wheel.group-batch-wheel-visible')
    .select('rect')
    .transition()
    .duration(animationTime)
    .ease(easing)
    .attr('y', d => {
      const hb = hoveredBatch.current
      const sb = scrollingBatch.current
      if (sb === d.b || hb === d.b) return d.r[2]
      return d.h === settings.sh ? d.d : d.r[0]
    })
    .attr('height', d => {
      const hb = hoveredBatch.current
      const sb = scrollingBatch.current
      if (sb === d.b || hb === d.b) return d.r[3]
      return d.h === settings.sh ? d.h : d.r[1]
    })

  // Answer variant pointers
  bs.selectAll('.group-batch-av-pointer')
    .transition()
    .duration(animationTime)
    .ease(easing)
    .attr('transform', d => `translate(${settings.bp},${d.y})`)

  // Answer variants
  bs.selectAll('.group-batch-av')
    .transition()
    .duration(animationTime)
    .ease(easing)
    .attr('transform', d => {
      const hb = hoveredBatch.current
      const sb = scrollingBatch.current
      const offset = sb === d.b || hb === d.b ? d.e : d.d
      return `translate(${settings.bp},${d.y + offset})`
    })
    .style('opacity', d => {
      const hb = hoveredBatch.current
      const sb = scrollingBatch.current
      if (sb === d.b || hb === d.b) return 1
      return d.v === 'visible' ? 1 : 0
    })
    .select('rect')
    .attr('y', d => -d.h / 2)
    .attr('height', d => d.h)
}

const expandBatch = (g, settings) => {
  g.selectAll('.group-batch-wheel')
    .select('rect')
    .attr('y', d => d.r[2])
    .attr('height', d => d.r[3])

  g.selectAll('.group-batch-av')
    .attr('transform', d => `translate(${settings.bp},${d.y + d.e})`)
    .style('opacity', 1)
}

const collapseBatch = (g, settings) => {
  g.selectAll('.group-batch-wheel')
    .select('rect')
    .attr('y', d => (d.h === settings.sh ? d.d : d.r[0]))
    .attr('height', d => (d.h === settings.sh ? d.h : d.r[1]))

  g.selectAll('.group-batch-av')
    .attr('transform', d => `translate(${settings.bp},${d.y + d.d})`)
    .style('opacity', d => (d.v === 'visible' ? 1 : 0))
}

// a canvas measures without laying the page out, so a chart with many answers pays no reflow per letter
const measureText = (text, fontSize) => {
  if (undefined === measureContext) measureContext = document.createElement('canvas').getContext('2d') || null
  if (!measureContext) return null

  measureContext.font = `${fontSize}px ${FONT_FAMILY}`
  return measureContext.measureText(text).width
}

const wrapText = (textEl, maxWidth, fullText, fontSize) => {
  textEl
    .text(null)
    .append('tspan')
    .attr('fill', textEl.attr('fill'))
    .attr('font-size', fontSize)
    .attr('font-family', FONT_FAMILY)
    .text(fitText(fullText, maxWidth, fontSize))
}

const findElement = (state, position, dy, currentPackage) => {
  const ws = state.ws[position - 1]
  const c = ws.indexOf(currentPackage[position - 1])
  const newC = Math.min(Math.max(c - dy, 0), ws.length - 1)
  return ws[newC]
}

const selectElement = (event, g, position, { state, scrollingBatch, onPackageChange, currentPackage }) => {
  scrollingBatch.current = position

  const point = d3.pointer(event, g.node())
  let startPoint = [...point]

  const onMove = moveEvent => {
    const currentPoint = d3.pointer(moveEvent, g.node())
    const dy = currentPoint[1] - startPoint[1]

    if (dy > 10 || dy < -10) {
      startPoint = currentPoint
      const newValue = findElement(state, position, Math.sign(dy), currentPackage)
      onPackageChange(position, newValue)
    }
  }

  const onUp = () => {
    scrollingBatch.current = 0
    document.removeEventListener('mousemove', onMove)
    document.removeEventListener('mouseup', onUp)
  }

  document.addEventListener('mousemove', onMove)
  document.addEventListener('mouseup', onUp)
}

const wheelElement = (event, position, { state, onPackageChange, currentPackage }) => {
  const newValue = findElement(state, position, Math.sign(event.deltaY), currentPackage)
  onPackageChange(position, newValue)
}

export const clearChart = svgEl => {
  if (!svgEl) return
  d3.select(svgEl).property(LAYOUT_KEY, null).selectAll('*').remove()
}

export const drawChart = (svgEl, opts) => {
  const { chartId, state, settings, mode, currentPackage, mainPackages, animationTime } = opts

  if (!state) return

  const svg = d3.select(svgEl)

  // A draw updates what is already there, so a package step animates the shapes in place instead of
  // rebuilding them. Sizes and the mode's labels are set only when a shape is created, so a change of
  // layout starts from an empty svg.
  const layout = `${settings.width}x${settings.height}:${mode}`
  if (svg.property(LAYOUT_KEY) !== layout) {
    svg.selectAll('*').remove()
    svg.property(LAYOUT_KEY, layout)
  }

  svg
    .attr('version', '1.1')
    .attr('xmlns:xlink', 'http://www.w3.org/1999/xlink')
    .attr('xmlns', 'http://www.w3.org/2000/svg')

  drawChartBase(svg, settings, chartId)
  drawChartPercents(svg, state, settings, mode)
  drawChartBatches(svg, state, settings, mode, opts)
  drawChartSidebar(svg, state, settings, currentPackage, mainPackages, opts)
  animateChart(svg, state, settings, animationTime, opts)
}

export const fitText = (fullText, maxWidth, fontSize) => {
  const text = fullText.trim()
  const width = measureText(text, fontSize)
  if (null == width || width <= maxWidth) return text

  // a longer prefix is never narrower, so the longest one that fits beside the ellipsis can be bisected
  const letters = Array.from(text)
  let fits = 0
  let overflows = letters.length
  while (fits + 1 < overflows) {
    const middle = Math.floor((fits + overflows) / 2)
    if (measureText(`${letters.slice(0, middle).join('')}${ELLIPSIS}`, fontSize) <= maxWidth) fits = middle
    else overflows = middle
  }

  return `${letters.slice(0, fits).join('')}${ELLIPSIS}`
}
